import { ArrowUpRight, CarFront, Heart, Share2 } from "lucide-react";

import { formatCurrency } from "@/lib/order";
import {
  getFuelLabel,
  getPublicItemPresentation,
} from "@/lib/public-item-presentation";
import type { PublicCompany, PublicProduct } from "@/types/store";
import styles from "./automotive.module.css";

type Props = {
  product: PublicProduct;
  company: PublicCompany;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onOpen: () => void;
};

export default function AutomotiveVehicleCard({
  product,
  company,
  isFavorite,
  onToggleFavorite,
  onOpen,
}: Props) {
  const presentation = getPublicItemPresentation(product, company);
  const attributes = product.business_attributes;
  const details = presentation.details.filter(
    (detail) =>
      detail.value && (detail.key !== "mileage" || attributes.mileage != null),
  );
  if (
    attributes.fuel_type &&
    !details.some((detail) => detail.key === "fuel")
  ) {
    details.push({
      key: "fuel",
      label: "Combustível",
      value: getFuelLabel(attributes.fuel_type),
    });
  }
  async function shareVehicle() {
    const slugPrefix =
      window.location.pathname === `/${company.slug}` ||
      window.location.pathname.startsWith(`/${company.slug}/`)
        ? `/${company.slug}`
        : "";

    const url =
      `${window.location.origin}${slugPrefix}/veiculo/${product.slug}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: product.name,
          url,
        });
        return;
      } catch (error) {
        if (
          error instanceof DOMException &&
          error.name === "AbortError"
        ) {
          return;
        }
      }
    }

    await navigator.clipboard.writeText(url);
  }

  const image = product.primary_image_url || product.images[0]?.image_url;
  const identity = [attributes.brand, attributes.model, attributes.version]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className={styles.card}>
      <div className={styles.cardMedia}>
        <button
          type="button"
          className={styles.imageButton}
          onClick={onOpen}
          aria-label={`Ver ${product.name}`}
        >
          {image ? (
            <img
              src={image}
              alt={product.name}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <CarFront size={64} aria-hidden="true" />
          )}
        </button>
        {!product.is_available ? (
          <span className={styles.status}>
            {product.availability_status === "sold"
              ? "Vendido"
              : "Indisponível"}
          </span>
        ) : product.is_featured ? (
          <span className={styles.status}>Destaque</span>
        ) : null}
        <button
          type="button"
          className={styles.favorite}
          aria-label={`${isFavorite ? "Remover" : "Adicionar"} ${product.name} ${isFavorite ? "dos" : "aos"} favoritos`}
          aria-pressed={isFavorite}
          onClick={onToggleFavorite}
        >
          <Heart
            size={19}
            fill={isFavorite ? "currentColor" : "none"}
            aria-hidden="true"
          />
        </button>
      </div>
      <div className={styles.cardBody}>
        <button
          type="button"
          className={styles.shareButton}
          onClick={shareVehicle}
          aria-label={`Compartilhar ${product.name}`}
          title="Compartilhar veículo"
        >
          <Share2 size={18} aria-hidden="true" />
        </button>

        {identity && <p className={styles.vehicleIdentity}>{identity}</p>}
        <h3>
          <button type="button" onClick={onOpen}>
            {product.name}
          </button>
        </h3>
        {details.length > 0 && (
          <dl className={styles.specs}>
            {details.map((detail) => (
              <div key={detail.key}>
                <dt>{detail.label}</dt>
                <dd>{detail.value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className={styles.cardBottom}>
          <div>
            {product.promotional_price && (
              <del className={styles.oldPrice}>
                {formatCurrency(product.price)}
              </del>
            )}
            <p className={styles.price}>
              {formatCurrency(product.current_price)}
            </p>
            {product.reference_code && (
              <p className={styles.reference}>Ref. {product.reference_code}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onOpen}
            className={styles.detailButton}
            aria-label={`Ver detalhes de ${product.name}`}
          >
            <ArrowUpRight size={22} aria-hidden="true" />
          </button>
        </div>
      </div>
    </article>
  );
}
